/**
 * McGinley Dynamic
 *
 * Self-adjusting moving average that automatically adjusts to market speed.
 * Tracks price more closely than traditional MAs during fast moves.
 * McGD = McGD_prev + (Price − McGD_prev) / (N × (Price/McGD_prev)⁴)
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource } from "./sma";

export const mcginleyIndicator: IndicatorDefinition = {
  id: "mcginley",
  name: "McGinley Dynamic",
  shortName: "MGD",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 2, max: 200 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#FF5722" },
  ],
  plots: [
    { key: "mcginley", label: "McGinley", color: "#FF5722", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source = "close" } = params;
    const src = getSource(candles, source);
    const data: { time: number; value: number }[] = [];

    if (src.length < 2) return [{ key: "mcginley", data }];

    let mgd = src[0];
    data.push({ time: candles[0].time, value: mgd });

    for (let i = 1; i < src.length; i++) {
      const ratio = mgd === 0 ? 1 : src[i] / mgd;
      mgd = mgd + (src[i] - mgd) / (period * Math.pow(ratio, 4));
      data.push({ time: candles[i].time, value: mgd });
    }

    return [{ key: "mcginley", data }];
  },
};
